import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { AlertTriangle, Info, HelpCircle } from 'lucide-react';
import { Button } from './Button';

// ===== STYLED COMPONENTS =====
const DialogOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(1px);
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
`;

const DialogContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  pointer-events: none;
`;

const DialogContent = styled.div<{ $isOpen: boolean; $variant?: 'default' | 'warning' | 'danger' }>`
  width: min(420px, calc(100vw - 32px));
  background: ${props => props.theme.background};
  border-radius: 16px;
  border: 1px solid ${props => {
    if (props.$variant === 'danger') return props.theme.destructive + '40';
    if (props.$variant === 'warning') return props.theme.warning + '40';
    return props.theme.border;
  }};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.98)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  overflow: hidden;
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const DialogIcon = styled.div<{ $variant?: 'default' | 'warning' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => {
    if (props.$variant === 'danger') return props.theme.destructive + '20';
    if (props.$variant === 'warning') return props.theme.warning + '20';
    return props.theme.primary + '20';
  }};
  color: ${props => {
    if (props.$variant === 'danger') return props.theme.destructive;
    if (props.$variant === 'warning') return props.theme.warning;
    return props.theme.primary;
  }};
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const DialogText = styled.div`
  flex: 1;
  min-width: 0;
`;

const DialogTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0 0 8px 0;
  line-height: 1.4;
`;

const DialogDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.mutedForeground};
  margin: 0;
  line-height: 1.5;
`;

const DialogFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.muted}20;
`;

// ===== TYPES =====
export type ConfirmationVariant = 'default' | 'warning' | 'danger';

export interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  description?: string;
  variant?: ConfirmationVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// ===== COMPONENT =====
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  description,
  variant = 'default',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onCancel, isLoading]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return <AlertTriangle />;
      case 'warning':
        return <AlertTriangle />;
      default:
        return <HelpCircle />;
    }
  };

  const getConfirmVariant = () => {
    switch (variant) {
      case 'danger':
        return 'destructive';
      case 'warning':
        return 'primary';
      default:
        return 'primary';
    }
  };

  return createPortal(
    <DialogOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <DialogContainer $isOpen={isOpen}>
        <DialogContent $isOpen={isOpen} $variant={variant}>
          <DialogHeader>
            <DialogIcon $variant={variant}>
              {getIcon()}
            </DialogIcon>
            <DialogText>
              <DialogTitle>{title}</DialogTitle>
              {description && (
                <DialogDescription>{description}</DialogDescription>
              )}
            </DialogText>
          </DialogHeader>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={getConfirmVariant() as any}
              onClick={onConfirm}
              disabled={isLoading}
              loading={isLoading}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogContainer>
    </DialogOverlay>,
    document.body
  );
};

